import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/shared/services/supabase';
import type { Location } from '@/shared/types/domain.types';

interface LocationFilterProps {
  selectedLocationIds: string[];
  onChange: (locationIds: string[]) => void;
  multiple?: boolean;
}

export function LocationFilter({
  selectedLocationIds,
  onChange,
  multiple = false,
}: LocationFilterProps) {
  const { data: locations, isLoading } = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data as Location[];
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (multiple) {
      const selectedOptions = Array.from(e.target.selectedOptions).map((opt) => opt.value);
      onChange(selectedOptions);
    } else {
      onChange(e.target.value ? [e.target.value] : []);
    }
  };

  if (isLoading) {
    return <div className="text-sm text-gray-500">Loading locations...</div>;
  }

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Location
      </label>
      <select
        multiple={multiple}
        value={multiple ? selectedLocationIds : selectedLocationIds[0] || ''}
        onChange={handleChange}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {!multiple && <option value="">All Locations</option>}
        {locations?.map((location) => (
          <option key={location.id} value={location.id}>
            {location.name}
          </option>
        ))}
      </select>
      {multiple && (
        <p className="mt-1 text-xs text-gray-500">Hold Ctrl/Cmd to select multiple</p>
      )}
    </div>
  );
}
